import React, { useEffect, useState } from "react";
import { useToggleUserStatus } from "utils/api/admin.api";
import { type UserTableRowProps } from "utils/types/usersManagement.type";
import toast from "react-hot-toast";
import avatar from "../../assets/images/avatar.png";
import UserWalletDetails from "components/userWalletDetails";

export default function UserTableRow({ user }: UserTableRowProps) {
  const [isActive, setIsActive] = useState(Boolean(user?.is_active));
  const [isExpanded, setIsExpanded] = useState(false);

  const { mutate: toggleStatus, isLoading: toggleLoading, isSuccess: toggleSuccess } =
    useToggleUserStatus();

  // Sync state with user prop
  useEffect(() => {
    setIsActive(Boolean(user?.is_active));
  }, [user?.is_active]);

  useEffect(() => {
    if (toggleSuccess) {
      toast.success("User status updated successfully!");
    }
  }, [toggleSuccess]);

  const handleToggleStatus = () => {
    toggleStatus(user.id);
  };

  const handleRowClick = (e: React.MouseEvent) => {
    // Don't toggle if clicking on the status toggle
    if ((e.target as HTMLElement).closest('label, input[type="checkbox"]')) {
      return;
    }
    setIsExpanded(!isExpanded);
  };

  return (
    <>
      <tr
        className="bg-black-300 cursor-pointer hover:bg-black-400 transition-colors"
        onClick={handleRowClick}
      >
        <td className="py-3 px-5 rounded-l-lg">
          <p className="text-black-900 text-xs">
            {new Date(user?.created_at).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </p>
        </td>
        <td className="py-3 px-2">
          <div className="flex items-center gap-2">
            {user?.image_url ? (
              <img
                src={user.image_url}
                alt={user.name}
                className="w-8 h-8 rounded-full object-cover"
              />
            ) : (
              <img
                src={avatar}
                alt={user.name}
                className="w-8 h-8 rounded-full object-cover"
              />
            )}
            <p className="text-black-900 text-xs">{user?.name}</p>
          </div>
        </td>
        <td className="py-3 px-2">
          <p className="text-black-900 text-xs font-medium underline">
            {user?.email}
          </p>
        </td>
        <td className="py-3 px-2 text-center">
          <span
            className={`text-xs px-2 py-1 rounded ${
              user?.is_verified
                ? "bg-green-100 text-green-800"
                : "bg-yellow-100 text-yellow-800"
            }`}
          >
            {user?.is_verified ? "Verified" : "Unverified"}
          </span>
        </td>
        <td className="py-3 px-2 text-center">
          <span
            className={`text-xs px-2 py-1 rounded ${
              user?.is_2fa_enabled
                ? "bg-blue-100 text-blue-800"
                : "bg-gray-100 text-gray-800"
            }`}
          >
            {user?.is_2fa_enabled ? "Enabled" : "Disabled"}
          </span>
        </td>
        <td className="py-3 px-2 rounded-r-lg text-center">
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={isActive}
              onChange={handleToggleStatus}
              disabled={toggleLoading}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-cyan-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-800"></div>
            <span className="ml-3 text-xs text-black-900">
              {isActive ? "Active" : "Inactive"}
            </span>
          </label>
        </td>
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan={6} className="p-0">
            <UserWalletDetails userId={user.id} />
          </td>
        </tr>
      )}
    </>
  );
}

